import React from 'react';
import { QueryClient } from '@tanstack/react-query';
import { AnyAction, Dispatch } from 'redux';

import { getCookie } from '@rover/rsdk/src/modules/Network/cookies';
import prefetchCountryConfigurationShim from '@rover/shared/js/prefetches/prefetchCountryConfigurationShim';
import prefetchCurrentUserShim from '@rover/shared/js/prefetches/prefetchCurrentUserShim';
import prefetchExperimentsShim from '@rover/shared/js/prefetches/prefetchExperimentsShim';
import prefetchFooterShim from '@rover/shared/js/prefetches/prefetchFooterShim';
import prefetchFrontendConfigurationShim from '@rover/shared/js/prefetches/prefetchFrontendConfigurationShim';
import prefetchFrontendEventsShim from '@rover/shared/js/prefetches/prefetchFrontendEventsShim';
import prefetchGlobalAnnouncementsShim from '@rover/shared/js/prefetches/prefetchGlobalAnnouncementsShim';
import prefetchHrefLangsShim from '@rover/shared/js/prefetches/prefetchHrefLangsShim';
import prefetchNotificationsCenterShim from '@rover/shared/js/prefetches/prefetchNotificationsCenterShim';
import prefetchServiceLinksShim from '@rover/shared/js/prefetches/prefetchServiceLinksShim';
import Route from '@rover/types/src/Route';

import { RouteObject } from '../../routes';

import BaseProvider from './BaseProvider';
import BaseTemplate from './BaseTemplate';

export type Props = {
  route: RouteObject;
};

const BaseTemplateContainer = ({ route: baseRoute }: Props): JSX.Element => {
  return (
    <BaseProvider>
      <BaseTemplate baseRoute={baseRoute} />
    </BaseProvider>
  );
};

BaseTemplateContainer.shimmedPrefetch = async (args: {
  context: Route;
  apiClient: QueryClient | Dispatch<AnyAction>;
}) => {
  const promises = [
    prefetchCountryConfigurationShim(args),
    prefetchCurrentUserShim(args),
    prefetchExperimentsShim(args),
    prefetchFooterShim(args),
    prefetchFrontendConfigurationShim(args),
    prefetchFrontendEventsShim(args),
    prefetchGlobalAnnouncementsShim(args),
    prefetchHrefLangsShim(args),
    prefetchServiceLinksShim(args),
  ];

  // Use the rli (rover logged in) cookie as a heuristic for auth status, without waiting for current-user.
  // Rely on the notifications API endpoint to reject requests if the user is not logged in.
  const hasRliCookie = !!getCookie('rli');

  if (hasRliCookie) {
    promises.push(prefetchNotificationsCenterShim(args));
  }

  return Promise.all(promises);
};

export default BaseTemplateContainer;
